/**
 * Pistes de prospection — port de admin.pistes.tsx, entièrement branché :
 * liste réelle filtrable, détail avec l'historique RÉEL des e-mails
 * automatiques (envoyés et programmés), ajout manuel, édition, cycle de statut.
 */
import { useEffect, useState } from 'react';
import { appelerApi, envoyer } from '../../reseau.ts';
import { Badge, Btn, Card, Chip, Input, Modal, PageTitle, SectionLabel, Select, TextArea } from '../ui.tsx';

type Piste = {
  id: string;
  type: 'organisateur_souhaite' | 'interet_entreprise' | 'inscription_organisateur';
  demandeur_nom: string | null;
  demandeur_email: string | null;
  demandeur_entreprise: string | null;
  organisateur_nom: string | null;
  organisateur_email: string | null;
  evenement_nom: string | null;
  message: string | null;
  statut: string;
  cree_le: string;
};

type EmailPiste = {
  destinataire: string;
  sujet: string;
  statut: string;
  envoyer_le: string;
  envoye_le: string | null;
  erreur: string | null;
  automatisation: string | null;
};

const LIBELLES_TYPE: Record<Piste['type'], string> = {
  organisateur_souhaite: 'Organisateur souhaité',
  interet_entreprise: 'Intérêt entreprise',
  inscription_organisateur: 'Inscription organisateur',
};

const STATUTS = ['nouvelle', 'en_cours', 'contactee', 'convertie', 'fermee'] as const;

const quandRelatif = (iso: string) => {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 60) return `il y a ${Math.max(1, min)} min`;
  if (min < 60 * 24) return `il y a ${Math.round(min / 60)} h`;
  return `il y a ${Math.round(min / 60 / 24)} j`;
};

type Brouillon = {
  type: Piste['type'];
  demandeur_nom: string;
  demandeur_email: string;
  demandeur_entreprise: string;
  organisateur_nom: string;
  organisateur_email: string;
  evenement_nom: string;
  message: string;
  statut: string;
};

const VIDE: Brouillon = {
  type: 'organisateur_souhaite',
  demandeur_nom: '',
  demandeur_email: '',
  demandeur_entreprise: '',
  organisateur_nom: '',
  organisateur_email: '',
  evenement_nom: '',
  message: '',
  statut: 'nouvelle',
};

export function Pistes() {
  const [pistes, setPistes] = useState<Piste[]>([]);
  const [typeFiltre, setTypeFiltre] = useState<string>('tous');
  const [detail, setDetail] = useState<Piste | null>(null);
  const [emails, setEmails] = useState<EmailPiste[]>([]);
  const [edition, setEdition] = useState<{ id: string | null; valeurs: Brouillon } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = async () => {
    const r = await appelerApi<{ pistes: Piste[] }>('/api/admin/pistes');
    if (r.donnees) {
      setPistes(r.donnees.pistes);
      setDetail((d) => r.donnees!.pistes.find((p) => p.id === d?.id) ?? r.donnees!.pistes[0] ?? null);
    }
  };

  useEffect(() => {
    void charger();
  }, []);

  useEffect(() => {
    if (!detail) {
      setEmails([]);
      return;
    }
    void appelerApi<{ emails: EmailPiste[] }>(`/api/admin/pistes/${detail.id}/emails`).then((r) => {
      if (r.donnees) setEmails(r.donnees.emails);
    });
  }, [detail?.id]);

  const lignes = pistes.filter((p) => typeFiltre === 'tous' || p.type === typeFiltre);

  const cycle = async (p: Piste) => {
    const suivant = STATUTS[(STATUTS.indexOf(p.statut as (typeof STATUTS)[number]) + 1) % STATUTS.length]!;
    await envoyer(`/api/admin/pistes/${p.id}/statut`, { statut: suivant });
    void charger();
  };

  const poser = (k: keyof Brouillon, v: string) =>
    setEdition((e) => (e ? { ...e, valeurs: { ...e.valeurs, [k]: v } } : e));

  const enregistrer = async () => {
    if (!edition) return;
    const v = edition.valeurs;
    const donnees = Object.fromEntries(Object.entries(v).filter(([, valeur]) => valeur.trim() !== ''));
    const r = edition.id
      ? await envoyer(`/api/admin/pistes/${edition.id}`, donnees, 'PUT')
      : await envoyer('/api/admin/pistes', donnees);
    if (r.erreur) {
      setErreur(r.erreur);
      return;
    }
    setErreur(null);
    setEdition(null);
    void charger();
  };

  return (
    <>
      <PageTitle
        title="Pistes de prospection"
        subtitle="Chaque demande venue de la vitrine, avec l'historique des e-mails déjà partis — ajoutez aussi vos pistes manuelles."
        action={
          <Btn variant="primary" onClick={() => setEdition({ id: null, valeurs: { ...VIDE } })}>
            Ajouter une piste
          </Btn>
        }
      />

      <div className="mb-5 flex flex-wrap gap-2">
        <Chip active={typeFiltre === 'tous'} onClick={() => setTypeFiltre('tous')}>
          Tous types
        </Chip>
        {(Object.keys(LIBELLES_TYPE) as Piste['type'][]).map((t) => (
          <Chip key={t} active={typeFiltre === t} onClick={() => setTypeFiltre(t)}>
            {LIBELLES_TYPE[t]}
          </Chip>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
        <div className="overflow-hidden rounded-[10px] bg-charbon">
          {lignes.map((p) => (
            <div
              key={p.id}
              onClick={() => setDetail(p)}
              className="block w-full cursor-pointer border-b border-graphite p-4 text-left transition-colors duration-200 last:border-b-0 hover:bg-[#262626]"
            >
              <p className="text-[15px] leading-relaxed text-fumee">
                <span className="text-blanc">{p.demandeur_nom ?? p.demandeur_email ?? 'Anonyme'}</span>
                {p.type === 'organisateur_souhaite' ? ' demande EventMatch à ' : " s'intéresse à "}
                <span className="text-blanc">
                  {p.organisateur_nom ?? p.organisateur_email ?? 'EventMatch.pro'}
                </span>
                {p.evenement_nom && <> pour « {p.evenement_nom} »</>} —{' '}
                {p.organisateur_email ?? p.demandeur_email} — {quandRelatif(p.cree_le)}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Badge>{LIBELLES_TYPE[p.type]}</Badge>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    void cycle(p);
                  }}
                  className="inline-flex cursor-pointer items-center rounded-full bg-blanc px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.05em] text-void"
                  title="Cliquer pour passer au statut suivant"
                >
                  {p.statut.replace('_', ' ')}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEdition({
                      id: p.id,
                      valeurs: {
                        type: p.type,
                        demandeur_nom: p.demandeur_nom ?? '',
                        demandeur_email: p.demandeur_email ?? '',
                        demandeur_entreprise: p.demandeur_entreprise ?? '',
                        organisateur_nom: p.organisateur_nom ?? '',
                        organisateur_email: p.organisateur_email ?? '',
                        evenement_nom: p.evenement_nom ?? '',
                        message: p.message ?? '',
                        statut: p.statut,
                      },
                    });
                  }}
                  className="text-[13px] text-fumee hover:text-blanc"
                >
                  Modifier
                </button>
              </div>
            </div>
          ))}
          {lignes.length === 0 && <p className="p-4 text-[14px] text-fumee">Aucune piste pour ce filtre.</p>}
        </div>

        {detail && (
          <Card>
            <SectionLabel>Détail de la piste</SectionLabel>
            <h2 className="mt-2 text-[18px] font-light text-blanc">
              {detail.demandeur_nom ?? detail.demandeur_email ?? 'Anonyme'}
            </h2>
            <p className="mt-1 text-[13px] text-fumee">
              {[detail.organisateur_nom, detail.organisateur_email ?? detail.demandeur_email]
                .filter(Boolean)
                .join(' · ')}
            </p>
            {detail.message && (
              <p className="mt-4 border-t border-graphite pt-4 text-[14px] leading-relaxed text-fumee">
                « {detail.message} »
              </p>
            )}
            <div className="mt-5 border-t border-graphite pt-4">
              <SectionLabel>E-mails automatiques</SectionLabel>
              {emails.length === 0 ? (
                <p className="mt-3 text-[13px] text-fumee">
                  Aucun e-mail programmé — aucune automatisation active ne correspond à ce type de
                  piste.
                </p>
              ) : (
                <ul className="mt-3 flex flex-col gap-3">
                  {emails.map((e, i) => (
                    <li key={i}>
                      <p className="text-[11px] uppercase tracking-[0.05em] text-fumee">
                        {e.statut === 'envoye'
                          ? `envoyé · ${quandRelatif(e.envoye_le!)}`
                          : e.statut === 'programme'
                            ? `programmé · ${new Date(e.envoyer_le).toLocaleDateString('fr-BE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
                            : e.statut}
                        {e.automatisation ? ` · ${e.automatisation}` : ''}
                      </p>
                      <p className="text-[14px] leading-snug text-blanc">« {e.sujet} »</p>
                      {e.erreur && (
                        <p className="text-[12px]" style={{ color: 'var(--em-erreur)' }}>
                          {e.erreur}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </Card>
        )}
      </div>

      {edition && (
        <Modal title={edition.id ? 'Modifier la piste' : 'Ajouter une piste'} onClose={() => setEdition(null)}>
          <div className="flex flex-col gap-4">
            <Select
              label="Type"
              value={edition.valeurs.type}
              onChange={(v) => poser('type', v)}
              options={(Object.keys(LIBELLES_TYPE) as Piste['type'][]).map((t) => ({
                valeur: t,
                texte: LIBELLES_TYPE[t],
              }))}
            />
            <Input label="Demandeur" value={edition.valeurs.demandeur_nom} onChange={(v) => poser('demandeur_nom', v)} placeholder="Pierre Dubois" />
            <Input label="E-mail du demandeur" value={edition.valeurs.demandeur_email} onChange={(v) => poser('demandeur_email', v)} type="email" placeholder="pierre@entreprise.be" />
            <Input label="Entreprise du demandeur" value={edition.valeurs.demandeur_entreprise} onChange={(v) => poser('demandeur_entreprise', v)} placeholder="Solutions Condroz" />
            <Input label="Organisateur ciblé" value={edition.valeurs.organisateur_nom} onChange={(v) => poser('organisateur_nom', v)} placeholder="CCI Liège" />
            <Input label="E-mail de l'organisateur" value={edition.valeurs.organisateur_email} onChange={(v) => poser('organisateur_email', v)} type="email" placeholder="contact@cci-liege.be" />
            <Input label="Événement" value={edition.valeurs.evenement_nom} onChange={(v) => poser('evenement_nom', v)} placeholder="Salon Entreprendre 2026" />
            <TextArea label="Message / contexte" value={edition.valeurs.message} onChange={(v) => poser('message', v)} rows={3} />
            {edition.id && (
              <Select
                label="Statut"
                value={edition.valeurs.statut}
                onChange={(v) => poser('statut', v)}
                options={STATUTS.map((s) => ({ valeur: s, texte: s.replace('_', ' ') }))}
              />
            )}
            {erreur && (
              <p className="text-[13px]" role="alert" style={{ color: 'var(--em-erreur)' }}>
                {erreur}
              </p>
            )}
            <div className="flex justify-end gap-3">
              <Btn onClick={() => setEdition(null)}>Annuler</Btn>
              <Btn variant="primary" onClick={() => void enregistrer()}>
                Enregistrer
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
